import { useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ErrorCode, IssueSummary } from '@jisy/shared';
import { ApiRequestError } from '../api/client.js';
import {
  fieldOptionsQuery,
  issueQuery,
  transitionsQuery,
  useIssueUpdate,
  useTransitionMutation,
} from '../api/queries.js';
import { Avatar, PriorityBadge, StatusBadge } from './Badges.js';
import styles from './IssueCellEdit.module.css';

/**
 * Правка статуса, исполнителя и приоритета прямо в строке таблицы.
 *
 * Ничего своего эти списки не умеют: значения и запись идут теми же маршрутами, что и в карточке
 * задачи, — статус меняется переходом рабочего процесса, исполнитель и приоритет правкой полей
 * с оптимистичной блокировкой. Поэтому и ограничения те же: доступны только переходы, которые
 * Jira разрешает для этой задачи, и только те исполнители, которых она разрешает назначить.
 *
 * Списки запрашиваются в момент открытия, а не заранее: строк на экране десятки, и держать
 * наготове переходы каждой значило бы обстреливать Jira запросами на каждой прокрутке. По той же
 * причине запросы и мутация живут внутри меню — пока список закрыт, у строки только состояние
 * «открыт ли он».
 */

/** Пункт «не назначена»: снятие исполнителя приходит тем же списком, что и назначение. */
const UNASSIGNED = 'unassigned';

/**
 * Момент последнего изменения задачи — то, на чём держится оптимистичная блокировка.
 *
 * Берётся из карточки, а не из строки: строка приходит из кэша Postgres и отстаёт ровно
 * настолько, насколько давно прошла фоновая синхронизация. Со значением из строки правка
 * упиралась бы в конфликт на каждой задаче, которую кто-то тронул в Jira после неё, — при том
 * что настоящего спора нет. Карточка читается прямо из Jira тем же запросом, что и в модальном
 * окне, и открытие списка ждёт её наравне со списком значений.
 *
 * После неудачи карточка перечитывается: следующая попытка должна идти со свежим `updated`,
 * иначе она упрётся в ту же ошибку.
 */
function useIssueWrite(issue: IssueSummary): {
  ifUpdated: string | null;
  loading: boolean;
  onError: () => void;
} {
  const queryClient = useQueryClient();
  const detail = useQuery(issueQuery(issue.key));

  return {
    ifUpdated: detail.data?.updated ?? issue.updated,
    loading: detail.isPending,
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ['issue', issue.key] });
    },
  };
}

interface MenuOption {
  id: string;
  /** Текст для поиска и для скринридера; в списке может рисоваться плашкой. */
  label: string;
  node?: React.ReactNode;
  hint?: string | undefined;
  selected: boolean;
}

export function StatusCell({ issue }: { issue: IssueSummary }): React.ReactElement {
  const { t } = useTranslation();

  return (
    <CellEditor
      label={t('table.editStatus', { key: issue.key })}
      display={<StatusBadge status={issue.status} />}
      editable={issue.updated !== null}
      renderMenu={(close) => <StatusMenu issue={issue} onDone={close} />}
    />
  );
}

export function AssigneeCell({ issue }: { issue: IssueSummary }): React.ReactElement {
  const { t } = useTranslation();

  return (
    <CellEditor
      label={t('table.editAssignee', { key: issue.key })}
      display={
        <>
          <Avatar user={issue.assignee} />
          <span className="truncate">{issue.assignee?.displayName ?? t('table.unassigned')}</span>
        </>
      }
      editable={issue.updated !== null}
      renderMenu={(close) => <AssigneeMenu issue={issue} onDone={close} />}
    />
  );
}

export function PriorityCell({ issue }: { issue: IssueSummary }): React.ReactElement {
  const { t } = useTranslation();

  return (
    <CellEditor
      label={t('table.editPriority', { key: issue.key })}
      display={<PriorityBadge priority={issue.priority} />}
      editable={issue.updated !== null}
      renderMenu={(close) => <PriorityMenu issue={issue} onDone={close} />}
    />
  );
}

/**
 * Ячейка-кнопка. Правка занимает ячейку целиком: попасть в плашку статуса на узкой колонке
 * трудно, а строка при этом продолжает открывать задачу — по любой другой колонке.
 *
 * Задача без `updated` не правится: без него нечего послать в оптимистичную блокировку,
 * и правка ушла бы вслепую поверх чужой.
 */
function CellEditor({
  label,
  display,
  editable,
  renderMenu,
}: {
  label: string;
  display: React.ReactNode;
  editable: boolean;
  renderMenu: (close: () => void) => React.ReactNode;
}): React.ReactElement {
  const [open, setOpen] = useState(false);

  if (!editable) return <>{display}</>;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={styles.trigger}
          aria-label={label}
          // Щелчок по строке открывает карточку задачи — правке поля это ни к чему.
          onClick={(event) => event.stopPropagation()}
        >
          {display}
          <span className={styles.chevron} aria-hidden="true">
            ▾
          </span>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className={styles.menu}
          align="start"
          sideOffset={4}
          // Портал уводит меню из строки в DOM, но не в дереве React: без этого выбор значения
          // всплыл бы до строки и открыл карточку задачи.
          onClick={(event) => event.stopPropagation()}
        >
          {renderMenu(() => setOpen(false))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function StatusMenu({ issue, onDone }: { issue: IssueSummary; onDone: () => void }): React.ReactElement {
  const { t } = useTranslation();
  const transitions = useQuery(transitionsQuery(issue.key));
  const mutation = useTransitionMutation(issue.key);
  const write = useIssueWrite(issue);

  const options: MenuOption[] = (transitions.data?.transitions ?? []).map((transition) => ({
    id: transition.id,
    label: transition.name,
    // Переход обычно называется по целевому статусу; если Jira его не назвала — берём имя перехода.
    node: transition.to ? <StatusBadge status={transition.to} /> : transition.name,
    // Переход с экраном требует полей, которых здесь не спросить: Jira такой запрос отклонит.
    hint: transition.hasScreen ? t('issue.transitionHasScreen') : undefined,
    selected: transition.to?.id === issue.status?.id,
  }));

  return (
    <MenuList
      loading={transitions.isPending || write.loading}
      empty={t('table.noTransitions')}
      options={options}
      pending={mutation.isPending}
      error={errorOf(mutation.error)}
      onPick={(id) => {
        if (!write.ifUpdated) return;
        mutation.mutate(
          { transitionId: id, ifUpdated: write.ifUpdated },
          { onSuccess: onDone, onError: write.onError },
        );
      }}
    />
  );
}

function AssigneeMenu({ issue, onDone }: { issue: IssueSummary; onDone: () => void }): React.ReactElement {
  const { t } = useTranslation();
  const fieldOptions = useQuery(fieldOptionsQuery(issue.key));
  const mutation = useIssueUpdate(issue.key);
  const write = useIssueWrite(issue);

  const options: MenuOption[] = [
    {
      id: UNASSIGNED,
      label: t('table.unassigned'),
      node: (
        <>
          <Avatar user={null} />
          <span className="truncate">{t('table.unassigned')}</span>
        </>
      ),
      selected: issue.assignee === null,
    },
    ...(fieldOptions.data?.assignees ?? []).map((person) => ({
      id: person.accountId,
      label: person.displayName,
      node: (
        <>
          <Avatar user={person} />
          <span className="truncate">{person.displayName}</span>
        </>
      ),
      selected: person.accountId === issue.assignee?.accountId,
    })),
  ];

  return (
    <MenuList
      loading={fieldOptions.isPending || write.loading}
      empty={t('table.noAssignees')}
      options={options}
      searchable
      pending={mutation.isPending}
      error={errorOf(mutation.error)}
      onPick={(id) => {
        if (!write.ifUpdated) return;
        mutation.mutate(
          { assigneeAccountId: id === UNASSIGNED ? null : id, ifUpdated: write.ifUpdated },
          { onSuccess: onDone, onError: write.onError },
        );
      }}
    />
  );
}

function PriorityMenu({ issue, onDone }: { issue: IssueSummary; onDone: () => void }): React.ReactElement {
  const { t } = useTranslation();
  const fieldOptions = useQuery(fieldOptionsQuery(issue.key));
  const mutation = useIssueUpdate(issue.key);
  const write = useIssueWrite(issue);

  const options: MenuOption[] = (fieldOptions.data?.priorities ?? []).map((priority) => ({
    id: priority.id,
    label: priority.name,
    node: <PriorityBadge priority={priority} />,
    selected: priority.id === issue.priority?.id,
  }));

  return (
    <MenuList
      loading={fieldOptions.isPending || write.loading}
      empty={t('table.noPriorities')}
      options={options}
      pending={mutation.isPending}
      error={errorOf(mutation.error)}
      onPick={(id) => {
        if (!write.ifUpdated) return;
        mutation.mutate(
          { priorityId: id, ifUpdated: write.ifUpdated },
          { onSuccess: onDone, onError: write.onError },
        );
      }}
    />
  );
}

/**
 * Список значений. Пока идёт запись, меню не закрывается: отказ Jira — обычное дело (нет прав,
 * задачу изменили, переход требует полей), и человек должен прочитать причину там же, где выбирал,
 * а не гадать, почему значение не изменилось.
 */
function MenuList({
  loading,
  empty,
  options,
  searchable = false,
  pending,
  error,
  onPick,
}: {
  loading: boolean;
  empty: string;
  options: MenuOption[];
  searchable?: boolean;
  pending: boolean;
  error: ErrorCode | null;
  onPick: (id: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, search]);

  if (loading) return <p className={styles.note}>{t('table.optionsLoading')}</p>;
  if (options.length === 0) return <p className={styles.note}>{empty}</p>;

  return (
    <>
      {searchable && (
        <input
          className={styles.search}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('filters.search')}
          aria-label={t('filters.search')}
          autoFocus
        />
      )}

      <div className={`${styles.list} ${pending ? styles.listPending : ''}`}>
        {visible.map((option) => (
          <button
            type="button"
            key={option.id}
            className={`${styles.option} ${option.selected ? styles.optionSelected : ''}`}
            disabled={pending}
            title={option.hint ?? option.label}
            onClick={() => onPick(option.id)}
          >
            <span className={styles.optionValue}>{option.node ?? option.label}</span>
            {option.hint && (
              <span className={styles.optionHint} aria-hidden="true">
                ↗
              </span>
            )}
            {option.selected && (
              <span className={styles.check} aria-hidden="true">
                ✓
              </span>
            )}
          </button>
        ))}
      </div>

      {pending && <p className={styles.note}>{t('table.saving')}</p>}
      {error && !pending && (
        <p className={styles.error} role="alert">
          {t(`errors.${error}` satisfies `errors.${ErrorCode}`)}
        </p>
      )}
    </>
  );
}

function errorOf(error: unknown): ErrorCode | null {
  if (error instanceof ApiRequestError) return error.code;
  return error ? 'INTERNAL' : null;
}
